import { useState, useEffect } from "react";
import { View, Text, Button } from "../base";
import { InputField } from "../base/InputField";
import { Icon } from "../base/Icon";
import { useAppDispatch } from "../../redux";
import { agentEnvironmentActions } from "../../redux/reducers/agentEnvironmentSlice";

export type WebhookSettingsFormData = {
  id?: string;
  agentId?: string;
  webhookUrl?: string | null;
  webhookSecret?: string | null;
};

type WebhookSettingsFormProps = {
  config: WebhookSettingsFormData;
  onChange: (key: string) => (value: unknown) => void;
};

export const WebhookSettingsForm = ({ config, onChange }: WebhookSettingsFormProps) => {
  const dispatch = useAppDispatch();
  const [copied, setCopied] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const [localUrl, setLocalUrl] = useState(config.webhookUrl || "");
  const [isSaving, setIsSaving] = useState(false);

  // Reset local state when config changes (e.g., navigating to different environment)
  useEffect(() => {
    setLocalUrl(config.webhookUrl || "");
    setIsSaving(false);
    setRevealedSecret(null);
  }, [config.id]);

  const hasChanges = localUrl.trim() !== (config.webhookUrl || "");

  const onSave = async () => {
    if (!config.id || !config.agentId) {
      console.error("Missing config.id or config.agentId", { id: config.id, agentId: config.agentId });
      return;
    }

    setIsSaving(true);
    const url = localUrl.trim();

    try {
      if (!url) {
        // Deleting URL and secret
        onChange("webhookUrl")(null);
        onChange("webhookSecret")(null);
        setRevealedSecret(null);
        await dispatch(
          agentEnvironmentActions.updateById({
            id: config.id,
            agentId: config.agentId,
            webhookUrl: null,
            webhookSecret: null,
          }),
        );
      } else if (!config.webhookSecret) {
        // Adding URL for the first time - generate and reveal secret
        onChange("webhookUrl")(url);
        await dispatch(
          agentEnvironmentActions.updateById({
            id: config.id,
            agentId: config.agentId,
            webhookUrl: url,
          }),
        );
        const secretResult = await dispatch(
          agentEnvironmentActions.regenerateWebhookSecret({
            id: config.id,
            agentId: config.agentId,
          }),
        );
        if (agentEnvironmentActions.regenerateWebhookSecret.fulfilled.match(secretResult)) {
          setRevealedSecret(secretResult.payload.webhookSecret);
        }
      } else {
        // Just updating URL (secret already exists)
        onChange("webhookUrl")(url);
        await dispatch(
          agentEnvironmentActions.updateById({
            id: config.id,
            agentId: config.agentId,
            webhookUrl: url,
          }),
        );
      }
    } catch (error) {
      console.error("Error saving webhook:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const onRegenerateSecret = async () => {
    if (config.id && config.agentId) {
      const result = await dispatch(
        agentEnvironmentActions.regenerateWebhookSecret({
          id: config.id,
          agentId: config.agentId,
        }),
      );
      if (agentEnvironmentActions.regenerateWebhookSecret.fulfilled.match(result)) {
        setRevealedSecret(result.payload.webhookSecret);
      }
    }
  };

  const onCopySecret = async () => {
    if (revealedSecret) {
      await navigator.clipboard.writeText(revealedSecret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const displaySecret = revealedSecret || (config.webhookSecret ? "•".repeat(40) : null);
  const isSecretRevealed = !!revealedSecret;

  return (
    <View className="space-y-6">
      <View className="space-y-2">
        <Text className="text-sm text-gray-600 dark:text-gray-400">
          Configure a webhook URL to receive notifications when calls end. We'll POST a JSON payload to this URL with session details.
        </Text>
      </View>

      <View className="space-y-4">
        <InputField
          label="Webhook URL"
          labelClassName="!w-full"
          className="!w-full"
          placeholder="https://your-api.com/webhooks"
          value={localUrl}
          onChange={setLocalUrl}
        />

        {displaySecret && (
          <View className="space-y-2">
            <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Webhook Secret</Text>
            <View className="flex items-center gap-2">
              <View className="flex-1 relative">
                <InputField
                  labelClassName="!w-full"
                  className="!w-full font-mono text-sm"
                  value={displaySecret}
                  readOnly
                />
              </View>
              {isSecretRevealed && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onCopySecret}
                  className="!p-2"
                >
                  <Icon
                    name={copied ? "check" : "copy"}
                    size={18}
                  />
                </Button>
              )}
              <Button
                type="button"
                variant="ghost"
                onClick={onRegenerateSecret}
                className="!p-2"
              >
                <Icon
                  name="refresh"
                  size={18}
                />
              </Button>
            </View>
            <Text className="text-xs text-gray-500 dark:text-gray-400">
              {isSecretRevealed ? "Copy this secret now. For security, it won't be shown again." : "Secret is hidden. Click regenerate to create a new secret."}
            </Text>
          </View>
        )}
      </View>

      <View className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg space-y-2">
        <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Webhook Payload</Text>
        <pre className="text-xs text-gray-600 dark:text-gray-400 overflow-x-auto">
          {`{
  "event": "session.ended",
  "sessionId": "sess_abc123",
  "agentId": "agent_xyz789",
  "environmentId": "env_456",
  "telephonyCallId": "call_abc123",
  "status": "completed",
  "createdAt": "2025-12-18T02:45:27.946Z",
  "fromNumber": "+15551234567", // caller
  "toNumber": "+15559876543",   // agent
  "duration": "120"             // seconds
}`}
        </pre>
        <Text className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          Headers: <code className="bg-gray-200 dark:bg-gray-800 px-1 rounded">X-Primvoices-Signature</code>,{" "}
          <code className="bg-gray-200 dark:bg-gray-800 px-1 rounded">X-Primvoices-Timestamp</code>
        </Text>
      </View>

      <View className="flex justify-end pt-2 border-t border-gray-200 dark:border-gray-700">
        <Button
          type="button"
          variant="brand"
          onClick={onSave}
          isDisabled={!hasChanges}
          isLoading={isSaving}
        >
          Save
        </Button>
      </View>
    </View>
  );
};
